import { useEffect, useState, useCallback, useRef } from "react";
import { FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, hasRole } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FinishedGoodsPanel } from "../components/inventory/FinishedGoodsPanel";
import { RawMaterialsPanel } from "../components/inventory/RawMaterialsPanel";
import { LedgerPanel } from "../components/inventory/LedgerPanel";
import { RecipeDialog } from "../components/inventory/RecipeDialog";

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [stockInFor, setStockInFor] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [selectedRaws, setSelectedRaws] = useState([]);
  const [recipeFor, setRecipeFor] = useState(null);
  const [rawForm, setRawForm] = useState({ name: "", unit: "kg", stock: "" });
  const rawFileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/inventory/overview").then(({ data }) => { setData(data); setSelectedRaws([]); }).catch((e) => toast.error(formatApiError(e)));
    if (isAdmin) api.get("/inventory/ledger").then(({ data }) => setLedger(data.entries)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const stockIn = async (kind, id, qty, note) => {
    const url = kind === "product" ? `/inventory/products/${id}/stock-in` : `/raw-materials/${id}/stock-in`;
    try {
      await api.post(url, { quantity: qty, note: note || null });
      toast.success("Stock added");
      setStockInFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const adjustStock = async (kind, id, newStock, note) => {
    const url = kind === "product" ? `/inventory/products/${id}/adjust` : `/raw-materials/${id}/adjust`;
    try {
      await api.post(url, { new_stock: newStock, note: note || null });
      toast.success("Stock corrected");
      setAdjustFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addRaw = async () => {
    if (!rawForm.name.trim()) { toast.error("Enter a raw material name"); return; }
    try {
      await api.post("/raw-materials", { name: rawForm.name.trim(), unit: rawForm.unit, stock: parseFloat(rawForm.stock) || 0 });
      toast.success("Raw material added");
      setRawForm({ name: "", unit: "kg", stock: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkDeleteRaws = async () => {
    if (!window.confirm(`Delete ${selectedRaws.length} selected raw material(s)? They will also be removed from recipes.`)) return;
    try {
      const { data } = await api.post("/raw-materials/bulk-delete", { ids: selectedRaws });
      toast.success(`${data.deleted} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteRaw = async (r) => {
    if (!window.confirm(`Delete raw material "${r.name}"? It will also be removed from recipes.`)) return;
    try {
      await api.delete(`/raw-materials/${r.id}`);
      toast.success("Raw material deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setMin = async (kind, id, v) => {
    const url = kind === "product" ? `/inventory/products/${id}/min-stock` : `/raw-materials/${id}/min-stock`;
    try {
      await api.post(url, { min_stock: v });
      toast.success("Minimum level saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportExcel = async () => {
    try {
      const { data } = await api.get("/inventory/export/excel", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "dcn_stock_report.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Stock report exported");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadRawTemplate = async () => {
    try {
      const { data } = await api.get("/raw-materials/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "dcn_raw_material_import_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const importRaws = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/raw-materials/import", fd);
      toast.success(`${data.added} added, ${data.skipped_existing} skipped (already exist)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!data) return <p className="p-8 text-center text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Inventory</h1>
          <p className="mt-1 text-sm text-slate-500">
            {isAdmin
              ? "Maintain finished goods and raw material stock. Stock is deducted automatically when orders are dispatched — dispatch is blocked if stock is insufficient."
              : "Log the finished goods you produce with Stock In. Stock is deducted automatically when orders are dispatched."}
          </p>
        </div>
        {isAdmin && (
          <button onClick={exportExcel} data-testid="inventory-export-button"
            className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
            <FileXls size={16} weight="duotone" /> Stock Report (Excel)
          </button>
        )}
      </div>

      <div className={`grid gap-6 ${isAdmin ? "xl:grid-cols-2" : ""}`}>
        <FinishedGoodsPanel products={data.products} isAdmin={isAdmin}
          adjustFor={adjustFor} setAdjustFor={setAdjustFor}
          stockInFor={stockInFor} setStockInFor={setStockInFor}
          adjustStock={adjustStock} stockIn={stockIn} setMin={setMin} setRecipeFor={setRecipeFor} />

        {isAdmin && (
          <RawMaterialsPanel rawMaterials={data.raw_materials}
            selectedRaws={selectedRaws} setSelectedRaws={setSelectedRaws}
            rawForm={rawForm} setRawForm={setRawForm} addRaw={addRaw}
            bulkDeleteRaws={bulkDeleteRaws} deleteRaw={deleteRaw}
            downloadRawTemplate={downloadRawTemplate} importRaws={importRaws} rawFileRef={rawFileRef}
            adjustFor={adjustFor} setAdjustFor={setAdjustFor}
            stockInFor={stockInFor} setStockInFor={setStockInFor}
            adjustStock={adjustStock} stockIn={stockIn} setMin={setMin} />
        )}
      </div>

      {isAdmin && <LedgerPanel ledger={ledger} />}

      {recipeFor && (
        <RecipeDialog product={recipeFor} rawMaterials={data.raw_materials}
          onClose={() => setRecipeFor(null)} onSaved={() => { setRecipeFor(null); load(); }} />
      )}
    </div>
  );
}
